import { useMemo } from "react";
import { kFormatter } from "hooks/utils/formattingUtils";
import "./selected-features-panel.css";
import "./stats-panel-container.css";

// Charts
import LineChart2 from "./charts/LineChart2";
import PieChart2 from "./charts/PieChart2";
import ColoredSVGChart from "./charts/ColoredSVGChart";

import layerGroups from "layers/layers";
import { LayerGroupName } from "types/dataModel";
import { Headline, Text } from "components/typography";
import { MapGeoJSONFeature } from "maplibre-gl";

function MetricContent({
  children,
  contentModifier,
}: {
  children: React.ReactNode;
  contentModifier?: string;
}) {
  return (
    <div
      className={
        "aeb-content-container " + (contentModifier ? contentModifier : "")
      }
    >
      {children}
    </div>
  );
}

function MetricTitle({
  title,
  Icon,
  selected = false,
  clickable = false,
}: {
  title: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  selected?: boolean;
  clickable?: boolean;
}) {
  return (
    <div
      className={
        "aeb-container-title" +
        (selected ? " selected" : "") +
        (clickable ? " clickable" : "")
      }
    >
      <div className="layer-image-container">
        <Icon fill="white" />
      </div>
      <div className="aeb-title-text-container">
        <Headline level={3} className="text-white my-1">
          {title}
        </Headline>
      </div>
    </div>
  );
}

function TemplateMetricContainer({
  title,
  icon,
  children,
  contentModifier,
  description = "Metric specific description to come here. Only shown when section is hovered.",
}: {
  title: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  children: React.ReactNode;
  contentModifier?: string;
  description?: string;
}) {
  return (
    <div className="aeb-container text-left">
      <MetricTitle Icon={icon} title={title} />
      <MetricContent contentModifier={contentModifier}>
        <Text
          level={2}
          className="aeb-container-metric-description px-2 text-neutral-midfi-grey"
        >
          {description}
        </Text>
        {children}
      </MetricContent>
    </div>
  );
}

const sum = (acc: number, cur: number) => acc + cur;

const reducers = {
  sum: (values: number[]) => values.reduce(sum, 0),
};

const getStat = (
  metric: string,
  selectedFeatures: MapGeoJSONFeature[],
  distinct = false,
) => {
  const values = selectedFeatures
    .map((x) => x.properties?.[metric])
    .filter((value) => value !== undefined && value !== null && !isNaN(value));
  const uniqueValues = distinct ? [...new Set(values)] : values;
  return reducers.sum(uniqueValues);
};

function SelectedFeaturesMetricsPanel({
  selectedFeatures,
  selectedYear,
}: {
  selectedFeatures: MapGeoJSONFeature[];
  selectedYear: number;
}) {
  const stockNoMangroves = useMemo(
    () =>
      getStat(`Ben_Stock_${selectedYear}`, selectedFeatures) +
      getStat(`Risk_Stock_${selectedYear}`, selectedFeatures),
    [selectedFeatures, selectedYear],
  );
  const stockWithMangroves = useMemo(
    () => getStat(`Risk_Stock_${selectedYear}`, selectedFeatures),
    [selectedFeatures, selectedYear],
  );
  const popNoMangroves = useMemo(
    () =>
      getStat(`Ben_Pop_${selectedYear}`, selectedFeatures) +
      getStat(`Risk_Pop_${selectedYear}`, selectedFeatures),
    [selectedFeatures, selectedYear],
  );
  const popWithMangroves = useMemo(
    () => getStat(`Risk_Pop_${selectedYear}`, selectedFeatures),
    [selectedFeatures, selectedYear],
  );
  const mangroves1996 = useMemo(
    () => getStat(`Mang_Ha_1996`, selectedFeatures),
    [selectedFeatures],
  );
  const mangroves2010 = useMemo(
    () => getStat(`Mang_Ha_2010`, selectedFeatures),
    [selectedFeatures],
  );
  const mangroves2015 = useMemo(
    () => getStat(`Mang_Ha_2015`, selectedFeatures),
    [selectedFeatures],
  );
  const mangroves2020 = useMemo(
    () => getStat(`Mang_Ha_2020`, selectedFeatures),
    [selectedFeatures],
  );
  const stock1996 = useMemo(
    () => getStat(`Ben_Stock_1996`, selectedFeatures),
    [selectedFeatures],
  );
  const stock2010 = useMemo(
    () => getStat(`Ben_Stock_2010`, selectedFeatures),
    [selectedFeatures],
  );
  const stock2015 = useMemo(
    () => getStat(`Ben_Stock_2015`, selectedFeatures),
    [selectedFeatures],
  );
  const stock2020 = useMemo(
    () => getStat(`Ben_Stock_2020`, selectedFeatures),
    [selectedFeatures],
  );

  const stock_risk_reduct_ratio = useMemo(() => {
    if (stockNoMangroves === 0) return 0;
    return (stockNoMangroves - stockWithMangroves) / stockNoMangroves;
  }, [stockNoMangroves, stockWithMangroves]);

  const piechart_pop_data = [
    { name: "Residual", value: popWithMangroves },
    { name: "Protected", value: popNoMangroves - popWithMangroves },
  ];

  const linechart_data = [
    { name: 1996, benefit: stock1996, mangroves: mangroves1996 },
    { name: 2010, benefit: stock2010, mangroves: mangroves2010 },
    { name: 2015, benefit: stock2015, mangroves: mangroves2015 },
    { name: 2020, benefit: stock2020, mangroves: mangroves2020 },
  ];

  const domain_gap = 0.01;

  const linechart_keys = [
    {
      id: "benefit",
      label: "Benefit",
      fill: "#13a5dc",
      axisOrientation: "left",
      tickFormatter: (tick: number) => {
        return "$" + kFormatter(tick);
      },
      domain: [
        Math.min(...linechart_data.map((d) => d.benefit)) * (1 - domain_gap),
        Math.max(...linechart_data.map((d) => d.benefit)) * (1 + domain_gap),
      ],
    },
    {
      id: "mangroves",
      label: "Mangroves",
      fill: "#da216d",
      axisOrientation: "right",
      tickFormatter: (tick: number) => {
        return kFormatter(tick) + " ha";
      },
      domain: [
        Math.min(...linechart_data.map((d) => d.mangroves)) * (1 - domain_gap),
        Math.max(...linechart_data.map((d) => d.mangroves)) * (1 + domain_gap),
      ],
    },
  ];

  const mangrove_disclaimer_text =
    "This analysis accounts changes in mangroves, population and coastal wealth over time. Flood risk benefits from mangroves grow signficantly with increases in coasal population and wealth.";

  return (
    <>
      <TemplateMetricContainer
        icon={layerGroups[LayerGroupName.BenefitAEB].IconComponent}
        description={layerGroups[LayerGroupName.BenefitAEB].shortDescription}
        title="Economic Benefit"
      >
        <>
          <div className="flex align-center flex-wrap w-full">
            <Text
              level={2}
              className="w-3/5 text-right text-neutral-midfi-grey mb-1"
            >
              Damages w/o Mangroves:
            </Text>
            <Text
              level={2}
              className="ml-2 lining-nums text-neutral-midfi-grey mb-1"
            >
              ${kFormatter(stockNoMangroves)}
            </Text>
            <Text
              level={2}
              className="w-3/5 text-right text-neutral-midfi-grey mb-1"
            >
              - Damages w/ Mangroves:
            </Text>
            <Text
              level={2}
              className="ml-2 lining-nums text-neutral-midfi-grey mb-1"
            >
              ${kFormatter(stockWithMangroves)}
            </Text>
            <div className="w-3/5 my-2 -translate-x-2 mx-auto pr-4 self-center border-solid border-t-[1px] border-primary-blue" />
            <Text
              level={2}
              className="w-3/5 text-right text-neutral-midfi-grey"
            >
              Total Damage Reduction:
            </Text>
            <Text
              level={2}
              className="ml-2 lining-nums font-bold text-neutral-midfi-grey"
            >
              ${kFormatter(stockWithMangroves - stockNoMangroves)}
            </Text>
          </div>
          <div className="flex row w-full pt-10 justify-start">
            <ColoredSVGChart
              risk_reduction_ratio={stock_risk_reduct_ratio}
              no_mang={stockNoMangroves}
              with_mang={stockWithMangroves}
            />
          </div>
        </>
      </TemplateMetricContainer>

      <TemplateMetricContainer
        icon={layerGroups[LayerGroupName.Population].IconComponent}
        description="Changes in mangrove extent and their economic benefits from 1996-2020."
        title="Changing Mangroves & Benefits"
      >
        <>
          <div className="flex align-right flex-wrap">
            <Text
              level={3}
              className="text-center px-2 text-neutral-midfi-grey"
            >
              {mangrove_disclaimer_text}
            </Text>
          </div>
        </>
        <LineChart2 data={linechart_data} keys={linechart_keys} />
      </TemplateMetricContainer>

      <TemplateMetricContainer
        icon={layerGroups[LayerGroupName.Population].IconComponent}
        description={layerGroups[LayerGroupName.Population].shortDescription}
        title="Population Benefit"
      >
        <>
          <div className="flex align-center flex-wrap">
            <Text
              level={2}
              className="w-4/5 text-center text-neutral-midfi-grey mb-1"
            >
              People flooded w/o Mangroves:
            </Text>
            <Text
              level={2}
              className="ml-2 lining-nums text-neutral-midfi-grey mb-1"
            >
              {kFormatter(popNoMangroves)}
            </Text>
            <Text
              level={2}
              className="w-4/5 text-center text-neutral-midfi-grey mb-1"
            >
              - People flooded w/ Mangroves:
            </Text>
            <Text
              level={2}
              className="ml-2 lining-nums text-neutral-midfi-grey mb-1"
            >
              {kFormatter(popWithMangroves)}
            </Text>
            <div className="w-4/5 my-2 -translate-x-2 mx-auto pr-4 self-center border-solid border-t-[1px] border-primary-blue" />
            <Text
              level={2}
              className="w-3/5 text-right text-neutral-midfi-grey"
            >
              Total Protection:
            </Text>
            <Text
              level={2}
              className="ml-2 lining-nums font-bold text-neutral-midfi-grey"
            >
              {kFormatter(popWithMangroves - popNoMangroves)}
            </Text>
          </div>
        </>
        <PieChart2 data={piechart_pop_data} type="STOCK" />
      </TemplateMetricContainer>
    </>
  );
}

export default SelectedFeaturesMetricsPanel;
